<template>
  <div class="home-page">
    <div class="container">


      <!-- Featured Sections -->
      <div class="sections-container">

        <!-- Physical Products & Buyback Hero Section -->
        <physical-home-section />

        <!-- Recently Viewed (Pick Up Where You Left Off) -->
        <RecommendedProductsSlider
          v-if="recentlyViewedStore.hasItems"
          title="🕒 Pick Up Where You Left Off"
          subtitle="Your recently viewed games, top-ups and hardware"
          badge="Browsing History"
          :items="recentlyViewedStore.all"
          :show-clear="true"
          @clear="recentlyViewedStore.clearAll"
        />

        <!-- Your Favorites Section (appears first if user has favorites) -->
        <section v-if="favoriteSubcategories.length > 0" class="category-section">
          <div class="section-header">
            <div class="section-header-content">
              <div class="section-title-row">
                <div class="section-icon favorites">
                  <svg viewBox="0 0 24 24" fill="currentColor" class="icon">
                    <path d="M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5C2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z"/>
                  </svg>
                </div>
                <div class="section-text">
                  <h2 class="section-title">Your Favorites</h2>
                  <p class="section-subtitle">Categories you love most</p>
                </div>
              </div>
            </div>
          </div>

          <div class="cards-grid">
            <subcategory-card
                v-for="subcategory in favoriteSubcategories"
                :key="'favorite-' + subcategory.id"
                :subcategory="subcategory"
                badge-type="favorite"
            />
          </div>
        </section>


        <!-- Best Sellers Section -->
        <section v-if="bestSellerSubcategories.length > 0" class="category-section">
          <div class="section-header">
            <div class="section-header-content">
              <div class="section-title-row">
                <div class="section-icon bestseller">
                  <svg viewBox="0 0 24 24" fill="currentColor" class="icon">
                    <path d="M5 16L3 5h5.5l1.5 5 1.5-5H16l-2 11H5zM12 2l3.09 6.26L22 9l-5 4.87 1.18 6.88L12 17.77l-6.18 2.98L7 14.87 2 9l6.91-1.74L12 2z"/>
                  </svg>
                </div>
                <div class="section-text">
                  <h2 class="section-title">🔥 Best Digital Sellers</h2>
                  <p class="section-subtitle">Most popular game cards, digital codes & instant automated activation</p>
                </div>
              </div>
            </div>
          </div>

          <div class="cards-grid">
            <subcategory-card
                v-for="subcategory in bestSellerSubcategories"
                :key="'best-seller-' + subcategory.id"
                :subcategory="subcategory"
                badge-type="best-seller"
            />
          </div>
        </section>

        <!-- New Arrivals Section -->
        <section v-if="newArrivalSubcategories.length > 0" class="category-section">
          <div class="section-header">
            <div class="section-header-content">
              <div class="section-title-row">
                <div class="section-icon new-arrival">
                  <svg viewBox="0 0 24 24" fill="currentColor" class="icon">
                    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4V12L16,16L17.4,14.6L14,11.2V4H12Z"/>
                  </svg>
                </div>
                <div class="section-text">
                  <h2 class="section-title">✨ New Digital Arrivals</h2>
                  <p class="section-subtitle">Fresh game credits, trending software subscriptions & latest releases</p>
                </div>
              </div>
            </div>
          </div>

          <div class="cards-grid">
            <subcategory-card
                v-for="subcategory in newArrivalSubcategories"
                :key="'new-arrival-' + subcategory.id"
                :subcategory="subcategory"
                badge-type="new-arrival"
            />
          </div>
        </section>

        <!-- Limited Offers Section -->
        <section v-if="limitedOfferSubcategories.length > 0" class="category-section">
          <div class="section-header">
            <div class="section-header-content">
              <div class="section-title-row">
                <div class="section-icon limited-offer">
                  <svg viewBox="0 0 24 24" fill="currentColor" class="icon">
                    <path d="M7,2V4H8V18A4,4 0 0,0 12,22A4,4 0 0,0 16,18V4H17V2H7M11,16C10.4,16 10,15.6 10,15C10,14.4 10.4,14 11,14C11.6,14 12,14.4 12,15C12,15.6 11.6,16 11,16M13,12C12.4,12 12,11.6 12,11C12,10.4 12.4,10 13,10C13.6,10 14,10.4 14,11C14,11.6 13.6,12 13,12M11,8C10.4,8 10,7.6 10,7C10,6.4 10.4,6 11,6C11.6,6 12,6.4 12,7C12,7.6 11.6,8 11,8Z"/>
                  </svg>
                </div>
                <div class="section-text">
                  <h2 class="section-title">⚡ Limited Flash Deals</h2>
                  <p class="section-subtitle">Special discount rates & exclusive promotional offers on digital products</p>
                </div>
              </div>
            </div>
          </div>

          <div class="cards-grid">
            <subcategory-card
                v-for="subcategory in limitedOfferSubcategories"
                :key="'limited-offer-' + subcategory.id"
                :subcategory="subcategory"
                badge-type="limited-offer"
            />
          </div>
        </section>

      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-content">
          <div class="loading-spinner"></div>
          <p class="loading-text">Loading categories...</p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!hasAnySubcategories" class="empty-state">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19,3H4.99C3.89,3 3.01,3.9 3.01,5L3,19C3,20.1 3.89,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M19,15H15C15,16.66 13.65,18 12,18C10.35,18 9,16.66 9,15H5V5H19V15Z"/>
          </svg>
        </div>
        <h3 class="empty-title">No Categories Available</h3>
        <p class="empty-description">Check back later for new categories</p>
      </div>

      <!-- Spacer -->
      <div class="spacer"></div>
    </div>

    <!-- Featured Physical Hardware Showcase (Before Customer Reviews) -->
    <featured-hardware-showcase />

    <!-- Testimonials Section (What our customer says) -->
    <testimonials-section />
  </div>
</template>

<script>
import { computed } from 'vue';
import SubcategoryCard from '../components/product/SubcategoryCard.vue';
import { useDataStore } from "@/stores/dataStore.js";
import TestimonialsSection from '../components/testimonials/TestimonialsSection.vue';
import PhysicalHomeSection from '../components/PhysicalHomeSection.vue';
import FeaturedHardwareShowcase from '../components/FeaturedHardwareShowcase.vue';
import RecommendedProductsSlider from '@/components/product/RecommendedProductsSlider.vue';
import { useRecentlyViewedStore } from '@/stores/recentlyViewedStore.js';

import { useSeo } from '@/composables/useSeo.js';

export default {
  name: 'HomePage',
  components: {
    SubcategoryCard,
    TestimonialsSection,
    PhysicalHomeSection,
    FeaturedHardwareShowcase,
    RecommendedProductsSlider
  },
  setup() {
    const dataStore = useDataStore();
    const recentlyViewedStore = useRecentlyViewedStore();
    const { updateSeo } = useSeo();

    const loading = computed(() => dataStore.loading);
    const subcategories = computed(() => {
        return dataStore.subcategories.filter(sub => ![33, 34, 35].includes(parseInt(sub.categorie_id)) && ![162, 163].includes(parseInt(sub.id)));
    });

    const favoriteIds = computed(() => dataStore.favoris || []);
    const favoriteSubcategories = computed(() => {
      if (!favoriteIds.value || favoriteIds.value.length === 0) return [];

      return subcategories.value.filter(sub =>
          favoriteIds.value.includes(sub.id)
      );
    });

    // Separate subcategories by type
    const bestSellerSubcategories = computed(() =>
        subcategories.value.filter(sub => sub.best_sellers_count > 0)
    );

    const limitedOfferSubcategories = computed(() =>
        subcategories.value.filter(sub => (sub.limited_offers_count > 0 && sub.best_sellers_count <= 0 ))
    );

    const newArrivalSubcategories = computed(() =>
        subcategories.value.filter(sub => (sub.new_arrivals_count > 0
            && sub.best_sellers_count <= 0
            && sub.limited_offers_count <= 0))
    );

    const regularSubcategories = computed(() =>
        subcategories.value.filter(sub =>
            sub.best_sellers_count === 0 &&
            sub.limited_offers_count === 0 &&
            sub.new_arrivals_count === 0
        )
    );

    const hasAnySubcategories = computed(() =>
        subcategories.value.length > 0
    );

    // Apply Homepage SEO
    updateSeo({
      title: 'Eskrag - Digital Gift Cards, Gaming Top-Ups & Certified Tech Hardware Store',
      description: 'Buy digital gift cards, mobile game recharges (Free Fire, PUBG, Steam, PlayStation), subscriptions, and certified laptops & consoles at Eskrag with instant 24/7 delivery and secure payment.',
      keywords: 'eskrag, gift cards algeria, free fire diamonds, pubg uc algeria, steam gift card, playstation card, certified refurbished laptops, buy ps5 algeria, buyback trade-in',
      ogTitle: 'Eskrag - Digital Cards, Gaming Top-Ups & Tech Store',
      ogDescription: 'Instant digital gift cards, game recharges, and certified computer hardware store with express delivery.',
      ogType: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'Eskrag',
        'url': typeof window !== 'undefined' ? window.location.origin : 'https://eskrag.com',
        'description': 'Digital Gift Cards, Gaming Top-Ups & Certified Tech Hardware Store in Algeria',
        'potentialAction': {
          '@type': 'SearchAction',
          'target': `${typeof window !== 'undefined' ? window.location.origin : 'https://eskrag.com'}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      jsonLdId: 'home-schema-jsonld'
    });

    return {
      loading,
      recentlyViewedStore,
      favoriteSubcategories,

      bestSellerSubcategories,
      limitedOfferSubcategories,
      newArrivalSubcategories,
      regularSubcategories,
      hasAnySubcategories
    };
  }
}
</script>


<style scoped>
/* Import modern fonts */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

/* Base styles */
.home-page {
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background-color: var(--color-background);
  color: var(--color-text);
  transition: all 0.3s ease;
  padding: 0.5rem 0 2rem 0;
}

/* Container for centering */
.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg,
  rgba(96, 0, 255, 0.1) 0%,
  rgba(0, 150, 36, 0.05) 100%);
  border-radius: 24px;
  padding: 3rem 2rem;
  margin-bottom: 3rem;
  border: 1px solid var(--color-background-lighter);
  transition: all 0.3s ease;
  text-align: center;
}

.dark .hero-section {
  background: linear-gradient(135deg,
  rgba(0, 230, 118, 0.15) 0%,
  rgba(0, 200, 83, 0.08) 100%);
}

.hero-content {
  max-width: 768px;
  margin: 0 auto;
}


.hero-title {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: var(--color-text);
  margin-bottom: 1rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.hero-description {
  font-size: 1.25rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

/* Sections Container */
.sections-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.category-section {
  opacity: 0;
  animation: fadeInUp 0.6s ease forwards;
}

.category-section:nth-child(1) { animation-delay: 0.1s; }
.category-section:nth-child(2) { animation-delay: 0.2s; }
.category-section:nth-child(3) { animation-delay: 0.3s; }
.category-section:nth-child(4) { animation-delay: 0.4s; }

/* Section Header - Centered */
.section-header {
  margin-bottom: 1.25rem;
  text-align: center;
}

.section-header-content {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.section-icon.favorites {
  background: linear-gradient(135deg, #ec4899, #db2777);
  color: white;
}

.section-icon.bestseller {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.section-icon.new-arrival {
  background: linear-gradient(135deg, var(--color-primary), var(--color-primary-dark));
  color: white;
}

.section-icon.limited-offer {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.section-icon.regular {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

.icon {
  width: 1rem;
  height: 1rem;
}

.section-text {
  flex: 1;
  text-align: left;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.25rem;
  letter-spacing: -0.01em;
}

.section-subtitle {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
}

/* Cards Grid - Responsive */
.cards-grid {
  display: grid;
  gap: 1.5rem;
  justify-content: center;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  max-width: 1200px;
  margin: 0 auto;
}

/* Loading State */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5rem 0;
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  width: 4rem;
  height: 4rem;
  border: 4px solid var(--color-background-lighter);
  border-top: 4px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem auto;
}

.loading-text {
  color: var(--color-text-secondary);
  font-size: 1rem;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 5rem 0;
}

.empty-icon {
  width: 6rem;
  height: 6rem;
  background-color: var(--color-background-light);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
}

.empty-icon svg {
  width: 1.5rem;
  height: 1.5rem;
  color: var(--color-text-secondary);
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.empty-description {
  color: var(--color-text-secondary);
  font-size: 1rem;
}

/* Spacer */
.spacer {
  margin: 4rem 0;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(2rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Mobile Responsive - 2-3 cards per row */
@media (max-width: 1024px) {
  .cards-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.25rem;
  }

  .hero-section {
    padding: 2rem 1.5rem;
  }

  .container {
    padding: 0 0.75rem;
  }
}

@media (max-width: 768px) {
  .sections-container {
    gap: 3rem;
  }

  .section-header-content {
    flex-direction: column;
    gap: 0.5rem;
  }

  /* 2-3 cards per row on mobile */
  .cards-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    max-width: 100%;
  }

  .hero-section {
    padding: 1.5rem 1rem;
    margin-bottom: 2rem;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-description {
    font-size: 1.125rem;
  }

  .container {
    padding: 0 0.5rem;
  }
}

@media (max-width: 640px) {
  .section-title-row {
    gap: 0.5rem;
    justify-content: center;
  }

  .section-icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .icon {
    width: 0.875rem;
    height: 0.875rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .section-text {
    text-align: center;
  }

  /* Ensure at least 2 cards per row even on smallest screens */
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
}

/* Ultra small screens */
@media (max-width: 360px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
}

/* Focus states for accessibility - Fixed to prevent whole group selection */
.card-link:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
  border-radius: 8px;
}

.category-section:focus-within {
  outline: none; /* Remove this to prevent group selection */
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .category-section {
    animation: none;
    opacity: 1;
  }

  .loading-spinner {
    animation: none;
  }
}
</style>
